import classnames from "classnames";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

import { Badge, Button, Heading, Span, TabGroup } from "@icg360/design-system";

import { screenSizes } from "consts";
import { usePolicyDetail } from "data/ss-data-hooks";
import { capitalizeEachWord, getPolicyStatus, trackEvent } from "utils";
import { getPolicyTypeLabel } from "utils/policy-type-labels";

import { PolicyDrawer } from "./policy-drawer/index";
import styles from "./policy-header.module.scss";

type PolicyHeaderProps = {
  tabs?: Record<string, string>[];
  pageTitle: string;
  isPolicyDrawerOpen: boolean;
  onPolicyDrawerOpen: () => void;
  onPolicyDrawerClose: () => void;
};

export const PolicyHeader = ({
  tabs,
  pageTitle,
  isPolicyDrawerOpen,
  onPolicyDrawerOpen,
  onPolicyDrawerClose,
}: PolicyHeaderProps) => {
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { data: policy, isLoading } = usePolicyDetail();
  const isMobile = useMediaQuery({
    maxWidth: screenSizes.laptop,
  });

  const activeTab = tabs?.findIndex((tab) => tab.link === currentPath);
  const showTabs = activeTab !== -1;

  const displayStatus = getPolicyStatus(policy?.userDetails?.policyStatus);
  const hasStatus = displayStatus !== "Unknown";

  // Format address for mobile with truncation if needed
  const formatMobileAddress = (): {
    street1: string;
    street2: string;
    hasStreet2: boolean;
  } => {
    const street1 = capitalizeEachWord(
      policy?.userDetails?.propertyAddress?.street1 ?? "",
    );
    const street2 = capitalizeEachWord(
      policy?.userDetails?.propertyAddress?.street2 ?? "",
    );

    if (!street1) {
      return { street1: "", street2: "", hasStreet2: false };
    }

    // If there's a street2 (apartment/suite), we need to truncate street1
    if (street2) {
      return { street1, street2, hasStreet2: true };
    }

    return { street1, street2: "", hasStreet2: false };
  };

  const mobileAddress: {
    street1: string;
    street2: string;
    hasStreet2: boolean;
  } = formatMobileAddress();

  return (
    <div className={styles.pageHeaderContainer}>
      <div className={styles.pagePrimaryHeader}>
        <div className={styles.pageHeadingWrapper}>
          <Heading size="lg" tag="h1">
            {pageTitle}
          </Heading>
        </div>
        <div
          className={classnames(
            styles.policyStatusWrapper,
            isLoading && styles.notLoaded,
          )}
          onClick={(e) => {
            if (isMobile && !(e.target as HTMLElement).closest("button")) {
              trackEvent("policySwitcher_mobileWrapper_clicked");
              onPolicyDrawerOpen();
            }
          }}
          onKeyDown={(e) => {
            if (
              isMobile &&
              (e.key === "Enter" || e.key === " ") &&
              !(e.target as HTMLElement).closest("button")
            ) {
              e.preventDefault();
              trackEvent("policySwitcher_mobileWrapper_clicked");
              onPolicyDrawerOpen();
            }
          }}
          role={isMobile ? "button" : "generic"}
          tabIndex={isMobile ? 0 : undefined}
        >
          <div className={styles.policyInfoContainer}>
            <div className={styles.policyInfoContent}>
              <div className={styles.addressRow}>
                <Span size="md" className={styles.policyAddress}>
                  <span className={styles.desktopAddress}>
                    {capitalizeEachWord(
                      policy?.userDetails?.propertyAddress?.street1 ?? "",
                    )}{" "}
                    {capitalizeEachWord(
                      policy?.userDetails?.propertyAddress?.street2 ?? "",
                    )}
                  </span>
                  <span className={styles.mobileAddress}>
                    {mobileAddress.hasStreet2 ? (
                      <>
                        <span className={styles.street1Truncated}>
                          {mobileAddress.street1}
                        </span>{" "}
                        {mobileAddress.street2}
                      </>
                    ) : (
                      mobileAddress.street1
                    )}
                  </span>
                  <span className={styles.desktopPolicyType}>
                    {" "}
                    -{" "}
                    {getPolicyTypeLabel(
                      policy?.userPolicyProperties?.policyType,
                    )}
                  </span>
                </Span>
                {hasStatus && (
                  <span className={styles.desktopPolicyBadge}>
                    <Badge
                      appearance={
                        displayStatus === "Active" ? "success" : "neutral"
                      }
                      size="compact"
                    >
                      {displayStatus}
                    </Badge>
                  </span>
                )}
              </div>
              <div className={styles.mobilePolicyTypeRow}>
                <Span size="sm" className={styles.policyType}>
                  {getPolicyTypeLabel(policy?.userPolicyProperties?.policyType)}
                </Span>
                {hasStatus && (
                  <span className={styles.policyBadge}>
                    <Badge
                      appearance={
                        displayStatus === "Active" ? "success" : "neutral"
                      }
                      size="compact"
                    >
                      {displayStatus}
                    </Badge>
                  </span>
                )}
              </div>
            </div>
            <div className={styles.policyButtonContainer}>
              <Button
                appearance="secondary"
                size="xs"
                onPress={onPolicyDrawerOpen}
                className={styles.changePolicyButton}
              >
                Change policies
              </Button>
              <Button
                appearance="tertiary"
                size="xs"
                icon="CaretRight"
                onPress={onPolicyDrawerOpen}
                aria-label="Change policies"
                className={styles.changePolicyIconButton}
              />
            </div>
          </div>
        </div>
      </div>
      {showTabs && (
        <div className={styles.headerTabs}>
          <TabGroup
            activeTabIndex={activeTab}
            border={false}
            tabs={tabs?.map((tab) => ({
              className: styles.tabItem,
              name: tab.name,
              onClick: () => {
                navigate(tab.link);
              },
            }))}
          />
        </div>
      )}
      <PolicyDrawer isOpen={isPolicyDrawerOpen} onClose={onPolicyDrawerClose} />
    </div>
  );
};
